import type { ComponentProps } from 'react';
import React from 'react';

import { Button } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { PetitionShareSlideUp } from 'src/app/pages/psf/promoteOrShare/shared/components';
import { useShareActions } from 'src/app/pages/psf/promoteOrShare/shared/hooks';
import {
	ShareEducationModalProvider,
	useShareEducationModal,
	// eslint-disable-next-line @change/sibling-module-imports
} from 'src/app/shared/hooks/share/actions/shared/shareAction/shareEducationModal';

type ButtonVariant = ComponentProps<typeof Button>['variant'];

type ShareSlideUpProps = {
	shareButtonLabel: string;
	buttonVariant?: ButtonVariant;
};

export const ShareSlideUp: React.FC<ShareSlideUpProps> = ({ shareButtonLabel, buttonVariant = 'secondary' }) => {
	const {
		data: { buttonConfigs, continueButtonProps, shareVisualUrl },
		actions: { handleOpenShareSlideUp, handleCloseShareSlideUp },
	} = useShareActions();

	const {
		data: { petition, psfComboRemoveAutoProgressionExperiment },
	} = usePsfPromoteOrSharePageData();

	const { variation } = psfComboRemoveAutoProgressionExperiment;
	const closeOnSocialShare = variation !== 'variant_2' && variation !== 'variant_3';

	/**
	 * Single modal instance shared across all channel share actions (instagram,
	 * instagramStories, tiktok).  Channel hooks read openModal from context and
	 * swap in their own content at open-time.
	 */
	const [openEducationModal, educationModal] = useShareEducationModal();

	return (
		<ShareEducationModalProvider value={openEducationModal}>
			<PetitionShareSlideUp
				petition={{
					ask: petition.ask,
					description: petition.description,
					photo: petition.photo?.petitionSmall.url,
				}}
				buttonConfigs={buttonConfigs}
				shareVisual={shareVisualUrl}
				closeOnSocialShare={closeOnSocialShare}
				trigger={
					<div className="w-full md:hidden">
						<Button
							className={cn('mt-2 w-full [&>div]:leading-5', { 'py-3': buttonVariant === 'tertiary' })}
							variant={buttonVariant}
							data-qa="share-button-slide-up"
							data-testid="share-button-slide-up"
							onClick={handleOpenShareSlideUp}
						>
							{shareButtonLabel}
						</Button>
					</div>
				}
				onClose={handleCloseShareSlideUp}
				continueButtonProps={continueButtonProps}
			/>
			{educationModal}
		</ShareEducationModalProvider>
	);
};
